import { CartUpdatedEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * Assistant Monday : questions suggérées + panneau de conversation + carte produit.
 *
 * À lire avant de modifier :
 *
 * 1. NON MODAL (pattern B : is-open / is-closing, 125 ms), jamais <dialog>.showModal(),
 *    qui rendrait le reste du document inerte (cf. le mur hCaptcha sur popup-optin).
 *    Le verrou de scroll n'est posé QU'EN MOBILE, où le panneau est plein écran.
 *
 * 2. Le panneau est déplacé dans <body> au montage : un position:fixed est piégé par
 *    tout ancêtre en transform / filter / will-change, qui devient son bloc conteneur.
 *    Le retrait est symétrique dans disconnectedCallback (sinon l'éditeur Shopify, qui
 *    re-rend la section, empilerait les panneaux).
 *
 * 3. Le JS NE FORMATE JAMAIS D'ARGENT. Le prix de la carte produit arrive DÉJÀ FORMATÉ
 *    du Liquid (`| money`, data-product-price), comme le fait iAdvize dont
 *    ProductOffer.price est une String. Theme.trailingZeros = false ferait perdre les
 *    décimales (« 1,00 € » → « 1 € »).
 *    Le serveur, lui, n'envoie aucune donnée produit : il n'émet qu'un signal nu. Il
 *    n'a pas de client Shopify, et lui en donner un pour afficher un prix que la page
 *    connaît déjà serait payer un aller-retour réseau pour perdre le formatage.
 *
 * 4. Le texte du modèle est ÉCHAPPÉ avant rendu. On n'accepte que **gras**. Injecter
 *    du HTML produit par un LLM dans le DOM est une XSS avec un intermédiaire.
 *
 * 5. Rien n'est écrit en localStorage avant le premier geste de l'utilisateur : c'est
 *    ce qui rend le stockage défendable en « strictement nécessaire » (cf. cookies.md).
 *
 * 6. UN TOUR BLOQUÉ « SANTÉ » N'EST JAMAIS PERSISTÉ, ni la question ni le refus. Le
 *    serveur s'interdit déjà d'en stocker le corps (ChatMessage.body reste nul, RGPD
 *    art. 9, donnée de catégorie particulière). Écrire sur le disque du visiteur ce que
 *    le serveur refuse de garder serait se donner raison d'un côté et tort de l'autre,
 *    et un poste partagé rendrait la contradiction lisible. Le client applique donc la
 *    même règle que le serveur.
 *
 * 7. L'ID DE CONVERSATION ET LE FIL VIVENT ET MEURENT ENSEMBLE. Reprendre l'id sans le
 *    fil (ce que faisait la v1 du stockage) rouvre un panneau vide en face d'un modèle
 *    qui, lui, a gardé la mémoire : le visiteur lit un message d'accueil neuf et
 *    s'entend répondre « comme je vous disais ». Toute lecture sans fil exploitable
 *    repart donc d'une conversation neuve.
 */

/**
 * Icônes. Tracés repris de la planche de validation (handoff « Assistant conversationnel
 * Monday »), stroke 1.5-1.7, viewBox 24. Ne pas remplacer par des glyphes texte : le
 * DS n'a pas de police d'icônes et un emoji est banni.
 */

/** Check. Sert au statut « Ajouté » et à la ligne de confirmation panier (3b). En cacao. */
const CHECK_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
  <path d="M20 6 9 17l-5-5"/>
</svg>`;

const MAIL_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
  <rect x="3" y="5" width="18" height="14"/><path d="M3 6l9 6 9-6"/>
</svg>`;

/** Bulle générique pour WhatsApp : PAS le logo, PAS de vert. Ce n'est pas notre marque. */
const WHATSAPP_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
  <path d="M20 15a2 2 0 0 1-2 2H7l-4 3V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z"/>
</svg>`;

/** Glyphe stop : pendant le streaming, le bouton d'envoi devient un bouton stop. */
const STOP_ICON = `<svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">
  <rect x="4.75" y="4.75" width="6.5" height="6.5" rx="1" fill="currentColor"/>
</svg>`;

/** Pouces. Même tracé retourné : un seul geste, deux sens. */
const THUMB_UP_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
  <path d="M7 22V10l5-8a2.5 2.5 0 0 1 2.5 3l-1 5H20a2 2 0 0 1 2 2.4l-1.6 7A2 2 0 0 1 18.4 22z"/><path d="M7 10H2v12h5"/>
</svg>`;

const THUMB_DOWN_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
  <path d="M17 2v12l-5 8a2.5 2.5 0 0 1-2.5-3l1-5H4a2 2 0 0 1-2-2.4l1.6-7A2 2 0 0 1 5.6 2z"/><path d="M17 14h5V2h-5"/>
</svg>`;

const STORAGE_KEY = 'monday:chat:v1';
// 24 h. Au-delà, le fil ET l'id sont oubliés (note 7) : une conversation d'hier n'est
// plus « en cours », et la reprendre en silence surprendrait plus qu'elle n'aiderait.
const STORAGE_TTL = 24 * 60 * 60 * 1000;
// Marge accordée à une horloge qui recule (recalage NTP, réglage manuel) entre l'écriture
// du fil et sa relecture. Assez large pour absorber un vrai décalage, assez courte pour
// qu'une entrée datée de l'an prochain reste jetée. Cf. store.read.
const CLOCK_TOLERANCE = 5 * 60 * 1000;
const TYPE_SPEED = 45; // ms par caractère. ~1,1 s sur « Une question sur Monday ? ».
const CLOSE_DELAY = 125; // Aligné sur side-menu.js / cart-drawer.js.
const CART_DISPATCH_DELAY = 180; // Cf. header-drawer-buybox.js : laisser le verrou de scroll retomber.
const MAX_LEN = 1000;
const SCROLL_STICK_THRESHOLD = 48; // px du bas au-delà desquels on ne force plus le scroll.
const WAIT_NOTICE_DELAY = 7000; // Sans premier delta au bout de 7 s : texte d'attente statique.
const ADDED_LABEL_DELAY = 2000; // Durée du label « Ajouté » sur la carte produit.

// Desktop = au moins 750px, aligné sur le breakpoint CSS du panneau (@media (width < 750px)).
const DESKTOP_MEDIA = window.matchMedia('(min-width: 750px)');

// Le CSS coupe déjà les animations sous cette préférence, mais la frappe est pilotée en
// JS : sans cette vérif, l'accroche s'écrirait quand même, caractère par caractère.
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

// Textes d'interface STATIQUES et relus. Rien ici n'est généré par le modèle.
const WAIT_NOTICE_TEXT = 'Je vérifie dans nos fiches produit.';
const PANEL_STATUS_FALLBACK = 'Réponse en quelques secondes';
const CONTACT_CTA_LABEL = 'Nous écrire';
const WHATSAPP_CTA_LABEL = 'WhatsApp';
const ADDED_LABEL = 'Ajouté';
const LABEL_ASSISTANT_FALLBACK = 'Assistant';
const LABEL_USER_FALLBACK = 'Vous';
const IN_STOCK_LABEL = 'en stock';
const OUT_OF_STOCK_LABEL = 'épuisé';
const CART_CONFIRM_PREFIX = 'Ajouté au panier';
const CTA_LABEL_FALLBACK = 'Ajouter';
const VOTE_LABEL = 'Cette réponse aide ?';
const VOTE_THANKS = 'Merci.';
const VOTE_UP_LABEL = 'Cette réponse aide';
const VOTE_DOWN_LABEL = "Cette réponse n'aide pas";
// Seul écran visible tant que les réponses par IA sont décochées. Écrit et relu.
const NOT_LIVE_TEXT =
  "L'assistant n'est pas encore ouvert. Écrivez-nous, notre équipe vous répond à la main.";

/** Échappe le HTML. Toute chaîne venant du modèle ou du réseau passe par là. */
function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = String(value ?? '');
  return div.innerHTML;
}

/**
 * Rendu minimal et sûr : échappement d'abord, puis **gras**, paragraphes et
 * listes « - item » / « 1. item » (ligne à ligne, les items acceptent le gras).
 * Volontairement pauvre : une liste blanche, pas un parseur Markdown.
 * Ni lien, ni image, ni HTML libre.
 */
function renderBotText(raw) {
  const bold = (text) => text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  const html = [];
  let para = [];
  let list = null;

  const flushPara = () => {
    if (!para.length) return;
    html.push(`<p>${para.map(bold).join('<br>')}</p>`);
    para = [];
  };
  const flushList = () => {
    if (!list) return;
    const items = list.items.map((item) => `<li>${bold(item)}</li>`).join('');
    html.push(`<${list.tag}>${items}</${list.tag}>`);
    list = null;
  };

  for (const rawLine of escapeHtml(raw).split('\n')) {
    const line = rawLine.trim();
    const bullet = line.match(/^-\s+(.+)/);
    const numbered = line.match(/^\d+\.\s+(.+)/);

    if (bullet || numbered) {
      const tag = bullet ? 'ul' : 'ol';
      flushPara();
      if (list && list.tag !== tag) flushList();
      if (!list) list = { tag, items: [] };
      list.items.push((bullet || numbered)[1]);
    } else if (line === '') {
      flushPara();
      flushList();
    } else {
      flushList();
      para.push(line);
    }
  }

  flushPara();
  flushList();
  return html.join('');
}

/**
 * localStorage défensif : Safari en navigation privée lève. On dégrade sans casser.
 *
 * Forme stockée : { id, at, turns: [{ role, text }] }. Tout est relu comme une entrée
 * HOSTILE, pas comme quelque chose qu'on aurait écrit : c'est du texte que n'importe
 * quelle extension peut réécrire, et les entrées de la version précédente du widget
 * (qui ne stockait que { id }) traînent encore chez les visiteurs de la semaine.
 */
const store = {
  read() {
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (!raw || typeof raw.id !== 'string' || !raw.id) return null;

      // Un `at` DANS LE FUTUR rendrait l'entrée immortelle : l'âge serait négatif, donc
      // éternellement sous le TTL (horloge système décalée, entrée trafiquée).
      //
      // Mais rejeter TOUT âge négatif est trop brutal, et ça s'est vu : un recalage NTP
      // de 800 ms entre l'écriture et la lecture suffisait à effacer une conversation en
      // cours. D'où la tolérance : en deçà, on considère que l'horloge a bougé sous nos
      // pieds et on garde le fil ; au-delà, on ne sait plus dater l'entrée et on la jette.
      const age = Date.now() - raw.at;
      if (!Number.isFinite(raw.at) || age < -CLOCK_TOLERANCE || age > STORAGE_TTL) return null;

      // Le fil est filtré tour par tour : une entrée trafiquée ne doit pas pouvoir
      // faire rendre autre chose que du texte dans un rôle connu.
      const turns = Array.isArray(raw.turns)
        ? raw.turns.filter(
            (turn) =>
              turn &&
              (turn.role === 'user' || turn.role === 'bot') &&
              typeof turn.text === 'string' &&
              turn.text
          )
        : [];

      // Sans fil, l'id ne vaut rien : le reprendre rouvrirait un panneau vide devant un
      // modèle qui se souvient (note 7). C'est aussi ce qui périme, sans code de
      // migration, les entrées { id } de la version précédente.
      if (!turns.length) return null;

      return { id: raw.id, turns };
    } catch (error) {
      return null;
    }
  },
  write(id, turns) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ id, at: Date.now(), turns }));
    } catch (error) {
      /* quota, navigation privée : on continue sans persistance */
    }
  },
  clear() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* idem */
    }
  },
};

class ChatWidget extends HTMLElement {
  #panel = null;
  #log = null;
  #input = null;
  #send = null;
  #composer = null;
  #close = null;
  #starterAsk = null;
  #chips = [];
  #busy = false;
  #abort = null;
  #conversationId = null;
  #scrollLocked = false;
  #stickToBottom = true;
  #jump = null;
  #sendIcon = '';
  #sendLabel = 'Envoyer';
  #followupsDone = false;
  #opener = null;

  // Animation d'entrée du bloc d'appel.
  #starter = null;
  #title = null;
  #typeOut = null;
  #typeText = '';
  #typeTimer = null;
  #observer = null;

  // Pastille de reprise.
  #fab = null;
  #fabOpen = null;
  #fabDismiss = null;
  #fabDismissed = false;

  // Le fil tel qu'il sera persisté. Miroir volontairement pauvre du DOM : { role, text }
  // et rien d'autre. Il ne contient JAMAIS de tour bloqué (note 6).
  #turns = [];
  #productDone = false;

  connectedCallback() {
    this.#panel = this.querySelector('[data-ref="panel"]');
    this.#log = this.querySelector('[data-ref="log"]');
    this.#input = this.querySelector('[data-ref="input"]');
    this.#send = this.querySelector('[data-ref="send"]');
    this.#composer = this.querySelector('[data-ref="composer"]');
    this.#close = this.querySelector('[data-ref="panel-close"]');
    this.#starterAsk = this.querySelector('[data-ref="starter-ask"]');
    this.#chips = Array.from(this.querySelectorAll('[data-ref="starter-chip"]'));
    this.#starter = this.querySelector('[data-ref="starter"]');
    this.#title = this.querySelector('[data-ref="starter-title"]');
    this.#typeOut = this.querySelector('[data-ref="type-out"]');
    this.#fab = this.querySelector('[data-ref="fab"]');
    this.#fabOpen = this.querySelector('[data-ref="fab-open"]');
    this.#fabDismiss = this.querySelector('[data-ref="fab-dismiss"]');

    if (!this.#panel) return;

    if (this.#send) {
      this.#sendIcon = this.#send.innerHTML;
      this.#sendLabel = this.#send.getAttribute('aria-label') || 'Envoyer';
    }

    // Sous-titre d'état sous le titre du panneau. Rendu ici depuis data-panel-status
    // (posé par le Liquid) : attribut présent mais vide = masqué volontairement.
    // Sans attribut (aperçu previews/, hors Liquid), on retombe sur le défaut du schema.
    const status = (this.dataset.panelStatus ?? PANEL_STATUS_FALLBACK).trim();
    if (status) {
      const statusEl = document.createElement('p');
      statusEl.className = 'chat-panel__status';
      statusEl.textContent = status;
      this.#panel.querySelector('.chat-panel__title')?.after(statusEl);
    }

    // Puce « revenir en bas » : flotte au-dessus du composer, visible seulement
    // quand l'utilisateur a quitté le bas du fil (scroll intelligent).
    this.#jump = document.createElement('button');
    this.#jump.type = 'button';
    this.#jump.className = 'chat-panel__jump';
    this.#jump.setAttribute('aria-label', 'Revenir aux derniers messages');
    this.#jump.textContent = '↓';
    this.#jump.hidden = true;
    this.#panel.querySelector('.chat-panel__foot')?.prepend(this.#jump);

    // Sortir le panneau de tout bloc conteneur potentiel (transform/filter d'un ancêtre).
    this.#panel.hidden = false;
    document.body.appendChild(this.#panel);

    // La pastille est fixed elle aussi : même piège, même traitement (note 2).
    if (this.#fab) document.body.appendChild(this.#fab);

    this.#jump.addEventListener('click', this.#onJumpClick);
    DESKTOP_MEDIA.addEventListener('change', this.#onBreakpointChange);
    REDUCED_MOTION.addEventListener('change', this.#onMotionChange);
    this.#log?.addEventListener('scroll', this.#onLogScroll, { passive: true });
    this.#chips.forEach((chip) => chip.addEventListener('click', this.#onChipClick));
    this.#starterAsk?.addEventListener('click', this.#onAskClick);
    this.#close?.addEventListener('click', this.#onCloseClick);
    this.#composer?.addEventListener('submit', this.#onSubmit);
    this.#log?.addEventListener('click', this.#onLogClick);
    this.#fabOpen?.addEventListener('click', this.#onFabOpenClick);
    this.#fabDismiss?.addEventListener('click', this.#onFabDismissClick);
    document.addEventListener('keydown', this.#onKeyDown);

    this.#restore();
    this.#armIntro();
  }

  disconnectedCallback() {
    this.#chips.forEach((chip) => chip.removeEventListener('click', this.#onChipClick));
    this.#starterAsk?.removeEventListener('click', this.#onAskClick);
    this.#close?.removeEventListener('click', this.#onCloseClick);
    this.#composer?.removeEventListener('submit', this.#onSubmit);
    this.#log?.removeEventListener('click', this.#onLogClick);
    this.#log?.removeEventListener('scroll', this.#onLogScroll);
    this.#jump?.removeEventListener('click', this.#onJumpClick);
    this.#fabOpen?.removeEventListener('click', this.#onFabOpenClick);
    this.#fabDismiss?.removeEventListener('click', this.#onFabDismissClick);
    DESKTOP_MEDIA.removeEventListener('change', this.#onBreakpointChange);
    REDUCED_MOTION.removeEventListener('change', this.#onMotionChange);
    document.removeEventListener('keydown', this.#onKeyDown);

    this.#abort?.abort();
    this.#unlockScroll();

    // L'observateur et la frappe survivraient au démontage : l'éditeur Shopify re-rend
    // la section à chaque frappe de l'admin, et on empilerait un timer par rendu.
    this.#observer?.disconnect();
    this.#observer = null;
    if (this.#typeTimer) clearInterval(this.#typeTimer);
    this.#typeTimer = null;

    // Symétrie : le panneau et la pastille vivent dans <body>, ils ne partiraient pas
    // avec la section.
    this.#panel?.remove();
    this.#fab?.remove();
  }

  get #live() {
    return this.dataset.live === 'true' && Boolean(this.dataset.endpoint);
  }

  get isOpen() {
    return Boolean(this.#panel?.classList.contains('is-open'));
  }

  // ------------------------------------------------------- animation d'entrée

  /**
   * Arme la frappe de l'accroche et la cascade des pilules, PUIS attend le viewport.
   *
   * Pourquoi un observateur et pas simplement du CSS au chargement : la cascade existait
   * déjà en CSS pur, mais elle partait au chargement du document alors que le bloc est
   * tout en bas de la colonne d'achat. Elle était donc systématiquement finie avant que
   * qui que ce soit arrive dessus. L'animation était là, le témoin n'y était pas.
   *
   * Rien n'est armé sous prefers-reduced-motion : ni `is-armed` (donc le CSS ne masque
   * rien), ni le vidage du texte. Le bloc rend exactement comme le Liquid l'a écrit.
   */
  #armIntro() {
    if (!this.#starter || REDUCED_MOTION.matches) return;

    this.#starter.classList.add('is-armed');

    // Le texte est LU depuis le DOM, pas dupliqué dans un attribut : il n'a qu'une
    // source, le setting Liquid. Le vider ici est ce qui rend la frappe possible ;
    // sans JS, il serait resté affiché, ce qui est exactement le comportement voulu.
    this.#typeText = this.#typeOut?.textContent?.trim() ?? '';
    if (this.#typeOut) this.#typeOut.textContent = '';

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          // Une seule fois par page : on débranche AVANT de jouer, sinon un aller-retour
          // de scroll relancerait la frappe sur une accroche déjà écrite.
          this.#observer?.disconnect();
          this.#observer = null;
          this.#playIntro();
        }
      },
      { threshold: 0.4 }
    );
    this.#observer.observe(this.#starter);
  }

  /** Frappe l'accroche, puis lâche la cascade des pilules et du CTA. */
  #playIntro() {
    // La préférence a pu basculer ENTRE le montage et l'entrée dans le viewport : le bloc
    // est en bas de page, il peut s'écouler des minutes. #armIntro a déjà vidé le texte,
    // donc ne pas revérifier ici taperait quand même, sous une préférence qui dit non.
    if (REDUCED_MOTION.matches || !this.#typeOut || !this.#typeText) {
      this.#finishIntro();
      return;
    }

    this.#title?.classList.add('is-typing');
    let index = 0;

    this.#typeTimer = setInterval(() => {
      index += 1;
      this.#typeOut.textContent = this.#typeText.slice(0, index);
      if (index < this.#typeText.length) return;
      this.#finishIntro();
    }, TYPE_SPEED);
  }

  /**
   * Pose l'état final : accroche entière, cascade lâchée. Idempotent, et c'est voulu :
   * c'est à la fois la fin normale de la frappe et la sortie de secours quand la
   * préférence de mouvement bascule au milieu.
   */
  #finishIntro() {
    if (this.#typeTimer) clearInterval(this.#typeTimer);
    this.#typeTimer = null;
    if (this.#typeOut && this.#typeText) this.#typeOut.textContent = this.#typeText;
    this.#title?.classList.remove('is-typing');
    this.#starter?.classList.add('is-revealed');
  }

  /**
   * La préférence bascule en cours de route (réglages système ouverts à côté, mode
   * économie qui l'active). Une préférence d'accessibilité doit prendre effet TOUT DE
   * SUITE, pas au prochain rechargement : on coupe la frappe et on pose le texte entier.
   */
  #onMotionChange = (event) => {
    if (event.matches) this.#finishIntro();
  };

  // ------------------------------------------------------- reprise du fil

  /**
   * Restaure une conversation en cours (id + fil) et lève la pastille de reprise.
   *
   * Le fil est re-rendu SOUS le message d'accueil, pas à sa place : l'accueil est un
   * repère de la marque, et le faire disparaître donnerait un panneau qui s'ouvre en
   * plein milieu d'une phrase.
   *
   * Les pouces ne sont pas rejoués sur un fil restauré. Un vote porte sur un message
   * précis et se donne dans l'instant ; le reproposer le lendemain, sur une réponse
   * qu'on relit à peine, appellerait un second vote que le serveur refuserait de toute
   * façon (chat_feedback.chat_message_id est unique).
   */
  #restore() {
    const saved = store.read();
    if (!saved) {
      // Entrée périmée, tronquée, ou de la version précédente du stockage : on nettoie
      // au lieu de la laisser traîner jusqu'à la prochaine tentative de lecture.
      store.clear();
      return;
    }

    this.#conversationId = saved.id;
    this.#turns = saved.turns;

    for (const turn of saved.turns) {
      if (turn.role === 'user') {
        this.#renderUser(turn.text);
      } else {
        const body = this.#appendBot();
        body.innerHTML = renderBotText(turn.text);
      }
    }

    // Le fil a déjà tourné : les suivis ont été proposés, ne pas les reproposer.
    this.#followupsDone = true;
    this.#showFab();
  }

  /** Persiste le fil. Appelé après chaque tour COMPLET, jamais pendant le streaming. */
  #persist() {
    if (!this.#conversationId || !this.#turns.length) return;
    store.write(this.#conversationId, this.#turns);
  }

  // ------------------------------------------------------- pastille de reprise

  #showFab() {
    if (!this.#fab || this.#fabDismissed) return;
    // Panneau ouvert : rien à rouvrir. Mais un panneau EN TRAIN de se fermer ne compte
    // pas comme ouvert : c'est justement le moment où la pastille doit revenir, et où
    // close() a besoin qu'elle soit là pour lui rendre le focus.
    if (this.isOpen && !this.#panel?.classList.contains('is-closing')) return;
    // Sans fil, la pastille n'a rien à rouvrir : le bloc d'appel est déjà la porte
    // d'entrée, et deux portes à deux centimètres l'une de l'autre n'en font pas une
    // meilleure.
    if (!this.#turns.length) return;
    this.#fab.hidden = false;
  }

  #hideFab() {
    if (this.#fab) this.#fab.hidden = true;
  }

  #onFabOpenClick = () => this.open();

  #onFabDismissClick = () => {
    // Le renvoi vaut pour la page, pas pour toujours : il dit « pas maintenant », pas
    // « oublie ma conversation ». Le fil reste en place, et le bloc d'appel de la
    // colonne rouvre le panneau si le visiteur change d'avis.
    this.#fabDismissed = true;
    this.#hideFab();
  };

  // ---------------------------------------------------------------- ouverture

  open() {
    if (!this.#panel || this.isOpen) return;

    // Mémorisé pour être rendu à la fermeture. Sans ça, le panneau part en
    // visibility:hidden avec le focus dedans, et le focus retombe sur <body> : au
    // clavier, le Tab suivant repart du haut du document au lieu de la colonne d'achat.
    this.#opener = document.activeElement;

    this.#panel.classList.remove('is-closing');
    this.#panel.classList.add('is-open');

    // La pastille n'a plus rien à rouvrir tant que le panneau est là, et elle se
    // retrouverait dessous, à moitié cachée par le coin de la carte.
    this.#hideFab();

    // Verrou de scroll SEULEMENT en mobile (panneau plein écran). En desktop le
    // panneau est non modal : la page doit rester lisible et défilable.
    if (isMobileBreakpoint()) {
      document.body.classList.add('overflow-hidden');
      this.#scrollLocked = true;
      this.#setBackgroundInert(true);
    }

    // Focus clavier UNIQUEMENT en desktop : en mobile, l'autofocus ouvrirait le
    // clavier par-dessus le message d'accueil avant qu'il soit lu.
    //
    // Mais « pas d'autofocus » ne veut pas dire « pas de focus » : le panneau est le
    // DERNIER enfant de <body>, donc laisser le focus sur la pilule cliquée obligeait
    // un utilisateur au clavier à tabuler à travers toute la page (footer compris) pour
    // atteindre le champ, et un lecteur d'écran continuait de lire la page cachée
    // derrière un panneau opaque. On focus le conteneur, sans ouvrir le clavier.
    if (DESKTOP_MEDIA.matches) {
      setTimeout(() => this.#input?.focus(), 150);
    } else {
      this.#panel.setAttribute('tabindex', '-1');
      setTimeout(() => this.#panel?.focus({ preventScroll: true }), 150);
    }
  }

  /**
   * Inerte le reste du document pendant que le panneau est plein écran (mobile).
   *
   * Ce n'est PAS un retour au modal proscrit par l'architecture : `inert` posé à la main
   * sur les frères n'a rien à voir avec <dialog>.showModal(), qui pose le top layer et
   * l'inertie au niveau du document (le mur hCaptcha). Ici on reste maître de la portée,
   * et desktop n'y touche jamais : le panneau y est non modal, la page reste utilisable.
   */
  #setBackgroundInert(on) {
    for (const el of document.body.children) {
      if (el === this.#panel) continue;
      if (on) {
        // Mémorise l'état d'origine : un frère peut être DÉJÀ inerte pour une autre
        // raison (cart drawer, popup), et on n'a pas à le réveiller en partant.
        if (el.hasAttribute('inert')) el.dataset.chatInertKept = '';
        else el.setAttribute('inert', '');
      } else if (el.dataset.chatInertKept !== undefined) {
        delete el.dataset.chatInertKept;
      } else {
        el.removeAttribute('inert');
      }
    }
  }

  close() {
    if (!this.#panel || !this.isOpen) return;
    this.#panel.classList.add('is-closing');
    this.#unlockScroll();

    // La pastille est rendue AVANT qu'on redonne le focus, et ce n'est pas un détail de
    // séquence : c'est souvent ELLE qui a ouvert le panneau, donc elle est l'`opener`,
    // et open() l'a masquée dans la foulée. Un élément `hidden` ne prend pas le focus :
    // le rendre à une pastille encore masquée, c'est le laisser retomber sur <body>, et
    // au clavier le Tab suivant repart du haut du document.
    // Elle réapparaît pendant le fondu de 125 ms, donc elle a l'air de récupérer le
    // panneau plutôt que de surgir après lui.
    this.#showFab();

    // Le focus revient d'où il venait, avant que le panneau ne devienne invisible.
    // `isConnected` : l'éditeur Shopify peut avoir re-rendu la section entre-temps.
    if (this.#opener?.isConnected && typeof this.#opener.focus === 'function') {
      this.#opener.focus();
    }
    this.#opener = null;

    setTimeout(() => {
      this.#panel?.classList.remove('is-open', 'is-closing');
    }, CLOSE_DELAY);
  }

  #unlockScroll() {
    if (!this.#scrollLocked) return;
    document.body.classList.remove('overflow-hidden');
    this.#setBackgroundInert(false);
    this.#scrollLocked = false;
  }

  /**
   * Le verrou de scroll est posé d'après le breakpoint AU MOMENT de l'ouverture, mais
   * le CSS, lui, suit la fenêtre en continu. Sans ce listener, ouvrir le panneau en
   * mobile puis élargir (rotation d'une tablette, redimensionnement) laissait le
   * panneau redevenir une carte non modale de 480px pendant que <body> restait figé
   * en overflow:hidden, sans plus rien pour le justifier ni l'expliquer.
   * Symétrique : repasser en mobile panneau ouvert doit reposer le verrou.
   */
  #onBreakpointChange = (event) => {
    if (!this.isOpen) return;
    if (event.matches) {
      this.#unlockScroll();
    } else if (!this.#scrollLocked) {
      document.body.classList.add('overflow-hidden');
      this.#setBackgroundInert(true);
      this.#scrollLocked = true;
    }
  };

  #onCloseClick = () => this.close();

  #onKeyDown = (event) => {
    if (event.key === 'Escape' && this.isOpen) this.close();
  };

  #onChipClick = (event) => {
    const question = event.currentTarget.dataset.question;
    this.open();
    if (question) this.#ask(question, 'chip');
  };

  #onAskClick = () => {
    this.open();
  };

  #onSubmit = (event) => {
    event.preventDefault();

    // Pendant le streaming, le bouton d'envoi est un bouton stop.
    if (this.#busy) {
      this.#abort?.abort();
      return;
    }

    const value = (this.#input?.value || '').trim();
    if (!value) return;
    this.#input.value = '';
    this.#ask(value, 'typed');
  };

  // ---------------------------------------------------------------- messages

  /**
   * Micro-libellé mono qui nomme le tour (« ASSISTANT » / « VOUS »). C'est la
   * signature de la direction 2b : le fil dit explicitement qui parle, ce qui
   * évite d'avoir à coder le bot en bulle (il reste du texte posé).
   */
  #who(kind) {
    const label =
      kind === 'user'
        ? this.dataset.labelUser || LABEL_USER_FALLBACK
        : this.dataset.labelAssistant || LABEL_ASSISTANT_FALLBACK;
    return `<p class="chat-msg__who">${escapeHtml(label)}</p>`;
  }

  /**
   * Dessin seul. Séparé de #appendUser pour que la restauration réutilise exactement le
   * même rendu sans ré-enregistrer un tour qui sort précisément du fil enregistré.
   */
  #renderUser(text) {
    const el = document.createElement('div');
    el.className = 'chat-msg chat-msg--user';
    el.innerHTML = `${this.#who('user')}<div class="chat-msg__body">${escapeHtml(text)}</div>`;
    this.#log?.appendChild(el);
    this.#scrollToEnd(true); // Geste explicite : on ré-ancre le fil en bas.
    return el;
  }

  #appendUser(text) {
    const turn = { role: 'user', text };
    this.#turns.push(turn);
    this.#renderUser(text);
    return turn;
  }

  /** Retire un tour du fil à persister. Sert au refus santé (note 6). */
  #dropTurn(turn) {
    const index = this.#turns.indexOf(turn);
    if (index !== -1) this.#turns.splice(index, 1);
  }

  #appendBot() {
    const el = document.createElement('div');
    el.className = 'chat-msg chat-msg--bot';
    el.innerHTML =
      this.#who('bot') +
      '<div class="chat-msg__body">' +
      '<span class="chat-typing" role="status" aria-label="L\'assistant écrit"><span></span><span></span><span></span></span>' +
      '</div>';
    this.#log?.appendChild(el);
    this.#scrollToEnd();
    return el.querySelector('.chat-msg__body');
  }

  /**
   * Message CALME sur surface bone : même traitement que le refus santé (3d).
   * Sert aux indisponibilités PRÉVUES (assistant pas encore activé), qui ne sont
   * ni une réponse ni une panne. Sortie de secours incluse, dans la boîte.
   */
  #appendNotice(message) {
    const el = document.createElement('div');
    el.className = 'chat-msg chat-msg--bot chat-msg--blocked';

    const box = document.createElement('div');
    box.className = 'chat-blocked';
    box.innerHTML = `<div class="chat-msg__body">${renderBotText(message)}</div>`;

    el.innerHTML = this.#who('bot');
    el.appendChild(box);
    this.#appendContactActions(box);
    this.#log?.appendChild(el);
    this.#scrollToEnd();
  }

  /**
   * Erreur TECHNIQUE (réseau, HTTP, stream mort). Rouge, parce que c'est un incident.
   * À ne pas confondre avec le refus santé (état `blocked`) ni avec une indisponibilité
   * prévue (#appendNotice), qui sont calmes et sur surface bone : Monday qui dit ce
   * qu'il n'est pas, ou qui n'est pas encore ouvert, n'est pas une panne.
   */
  #appendError(message) {
    const el = document.createElement('div');
    el.className = 'chat-msg chat-msg--bot chat-msg--error';
    el.innerHTML = `${this.#who('bot')}<div class="chat-msg__body">${renderBotText(message)}</div>`;
    this.#appendContactActions(el);
    this.#log?.appendChild(el);
    this.#scrollToEnd();
  }

  /**
   * Scroll intelligent : ne coller au bas que si l'utilisateur y était déjà.
   * Sinon chaque delta le ramènerait de force pendant qu'il relit une précaution :
   * on montre la puce « revenir en bas » à la place. `force` = geste explicite
   * (envoi d'un message, clic sur la puce), qui ré-ancre le fil.
   */
  #scrollToEnd(force = false) {
    if (!this.#log) return;
    if (force) this.#stickToBottom = true;
    if (!this.#stickToBottom) {
      if (this.#jump) this.#jump.hidden = false;
      return;
    }
    this.#log.scrollTop = this.#log.scrollHeight;
    if (this.#jump) this.#jump.hidden = true;
  }

  #onLogScroll = () => {
    if (!this.#log) return;
    const distance = this.#log.scrollHeight - this.#log.scrollTop - this.#log.clientHeight;
    this.#stickToBottom = distance <= SCROLL_STICK_THRESHOLD;
    if (this.#jump) this.#jump.hidden = this.#stickToBottom;
  };

  #onJumpClick = () => {
    this.#scrollToEnd(true);
  };

  #setBusy(busy) {
    this.#busy = busy;
    if (this.#input) this.#input.disabled = busy;
    if (this.#send) {
      // Pendant le streaming, le bouton d'envoi devient un bouton stop : il
      // reste cliquable, seuls le glyphe et le label changent.
      this.#send.innerHTML = busy ? STOP_ICON : this.#sendIcon;
      this.#send.setAttribute('aria-label', busy ? 'Arrêter la réponse' : this.#sendLabel);
    }
    // L'état « occupé » est porté par l'indicateur de frappe (3a), pas par le point
    // de présence : celui-ci pulse en permanence, c'est le fil conducteur entre le
    // bloc d'appel et l'en-tête du panneau (3e). Rien à basculer ici.
  }

  // ---------------------------------------------------------------- échange

  async #ask(question, source = 'typed') {
    if (this.#busy) return;

    const text = question.slice(0, MAX_LEN);

    if (!this.#live) {
      // Mode chips seules : aucun appel serveur, aucun coût, aucun risque.
      //
      // Calme, PAS rouge. Le rouge (#appendError) dit « incident » : il est réservé à
      // ce qui a vraiment cassé. Ici rien n'est cassé, l'assistant n'est simplement
      // pas encore ouvert, et c'est un état PRÉVU. Tant que enable_ai est décoché,
      // c'est le seul écran que voient les visiteurs : le peindre en erreur ferait
      // passer une mise en attente pour une panne sur une page qui vend.
      //
      // Le tour est DESSINÉ mais pas ENREGISTRÉ : sans backend il n'y a pas de
      // conversation, donc rien à reprendre. L'enregistrer lèverait une pastille de
      // reprise qui rouvrirait un panneau sur un assistant fermé.
      this.#renderUser(text);
      this.#appendNotice(NOT_LIVE_TEXT);
      return;
    }

    const userTurn = this.#appendUser(text);
    this.#setBusy(true);
    const body = this.#appendBot();
    let buffer = '';
    let first = true;
    let blocked = false;

    // Le tour est-il DÉJÀ arbitré (enregistré, ou volontairement écarté) ?
    //
    // Sans ce drapeau, il existe une fenêtre réelle entre `done` (qui enregistre déjà) et
    // la fermeture effective du flux : le bouton stop y est encore armé, parce que
    // #setBusy(false) n'arrive qu'au `finally`. Un clic dedans rejette `reader.read()`
    // en AbortError et la branche de rattrapage réenregistre le même tour. Résultat :
    // la réponse en double au rechargement, alors que le fil à l'écran n'en montre qu'une.
    //
    // Et le cas grave n'est pas le doublon. Le filtre de sortie du serveur peut trancher
    // APRÈS avoir laissé passer des deltas : au moment du `blocked`, `buffer` contient
    // donc le DÉBUT d'une réponse santé refusée. Un stop dans la fenêtre l'écrirait sur
    // le disque du visiteur, ce que le serveur s'interdit précisément (note 6).
    let settled = false;

    // Palier d'attente : sans premier delta au bout de 7 s, les trois points
    // deviennent un texte statique RELU (jamais généré). `first` reste vrai :
    // le premier delta repartira d'une bulle vide, comme avec les trois points.
    const waitTimer = setTimeout(() => {
      if (first) body.innerHTML = renderBotText(WAIT_NOTICE_TEXT);
    }, WAIT_NOTICE_DELAY);

    this.#abort = new AbortController();

    try {
      const response = await fetch(`${this.dataset.endpoint}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({
          conversation_id: this.#conversationId,
          message: text,
          context: this.#pageContext(source),
        }),
        signal: this.#abort.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`HTTP ${response.status}`);
      }

      for await (const event of this.#readEvents(response.body)) {
        switch (event.type) {
          case 'meta':
            // L'id est RETENU ici, mais rien n'est encore écrit sur le disque : le
            // stockage n'a lieu qu'au tour complet (#persist, sur `done`), parce que
            // l'id et le fil vivent et meurent ensemble (note 7). Un id écrit seul,
            // c'est exactement ce que faisait la version précédente, et c'est ce qui
            // rouvrait un panneau vide devant un modèle qui se souvenait.
            this.#conversationId = event.conversation_id;
            break;

          case 'delta':
            clearTimeout(waitTimer);
            if (first) {
              body.innerHTML = '';
              first = false;
            }
            buffer += event.text;
            body.innerHTML = renderBotText(buffer);
            this.#scrollToEnd();
            break;

          case 'product':
            // Signal NU : le serveur dit « c'est le moment », il ne dit pas quoi. Les
            // données sortent du Liquid (note 7 du bloc), parce que le service n'a
            // aucun client Shopify et que le JS n'a pas le droit de formater un prix.
            this.#appendProduct();
            break;

          case 'blocked': {
            // 3d · refus santé. Calme, sur surface bone, JAMAIS rouge : le modèle
            // dit ce que Monday n'est pas, ce n'est pas un incident. Le déclenchement
            // est côté backend (déjà filtré) ; ici on ne fait que l'habiller.
            clearTimeout(waitTimer);
            body.innerHTML = renderBotText(event.message);
            first = false;
            blocked = true;
            settled = true;
            body.parentElement?.classList.add('chat-msg--blocked');

            // Le tour sort du fil à persister, question COMPRISE (note 6). Le serveur
            // ne stocke pas le corps d'un message santé ; on n'a pas à l'écrire sur le
            // disque du visiteur à sa place. L'échange reste à l'écran tant que le
            // panneau est ouvert, il ne survit simplement pas au rechargement.
            this.#dropTurn(userTurn);

            // La surface bone est une BOÎTE autour du texte ET des sorties de secours
            // (cf. planche 3d) : le micro-libellé « ASSISTANT » reste au-dessus, dehors.
            // Les boutons sont dans la boîte mais HORS de .chat-msg__body, sinon la
            // reprise de sous-arbre (note 4 du bloc) les repeindrait en Inter Tight et
            // tuerait leur mono uppercase.
            const box = document.createElement('div');
            box.className = 'chat-blocked';
            body.replaceWith(box);
            box.appendChild(body);
            this.#appendContactActions(box);
            this.#scrollToEnd();
            break;
          }

          case 'done':
            if (first) body.innerHTML = renderBotText('…');
            settled = true;
            if (!blocked) {
              // Le tour n'est enregistré qu'ICI, complet. Persister à chaque delta
              // écrirait des dizaines de fois en localStorage pour une seule réponse.
              if (buffer) {
                this.#turns.push({ role: 'bot', text: buffer });
                this.#persist();
              } else {
                // Réponse vide : il n'y a rien à reprendre. Garder la question seule la
                // ferait relire, demain, comme une question que l'assistant a ignorée.
                this.#dropTurn(userTurn);
              }
              // Les pouces reviennent (retirés au lot A). Le chemin serveur n'avait
              // jamais bougé : `message_id` est envoyé depuis le début, et l'onglet
              // Chat du dashboard agrège déjà les notes. Il ne manquait que l'appel.
              // Jamais sur un refus : le serveur n'y attache pas de message_id, et on
              // ne fait pas voter quelqu'un sur un refus.
              this.#appendVote(body.parentElement, event.message_id);
              this.#appendFollowups();
            }
            break;

          case 'error':
            throw new Error(event.message || 'stream error');
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // Stop volontaire : le texte partiel déjà streamé est CONSERVÉ. S'il
        // n'y a encore rien (trois points ou palier d'attente), on retire la bulle.
        if (settled) {
          // Le tour a déjà été arbitré (enregistré par `done`, ou écarté par `blocked`)
          // et le stop n'est arrivé qu'après : il n'y a rien à rattraper. Réenregistrer
          // ici doublerait la réponse, ou écrirait le début d'un refus santé.
        } else if (first) {
          body.parentElement?.remove();
          // Une question sans le moindre début de réponse ne va pas dans le fil : au
          // rechargement, elle se lirait comme si l'assistant l'avait laissée passer,
          // et la réponse SUIVANTE se lirait comme la sienne.
          this.#dropTurn(userTurn);
        } else {
          // `done` ne viendra pas : c'est le seul endroit où enregistrer ce que le
          // visiteur a réellement sous les yeux. Sans ça, un fil repris demain
          // afficherait une question sans la réponse partielle qu'il avait lue.
          this.#turns.push({ role: 'bot', text: buffer });
          this.#persist();
        }
      } else {
        body.parentElement?.remove();
        // Même raison que ci-dessus : le fil ne garde que des échanges complets. Sans ce
        // retrait, le #persist() du tour SUIVANT écrirait cette question orpheline, et
        // la réponse d'après viendrait se coller dessous comme si elle lui répondait.
        this.#dropTurn(userTurn);
        this.#appendError(
          "Je n'ai pas réussi à répondre. Réessayez, ou écrivez-nous et on vous répond à la main."
        );
      }
    } finally {
      clearTimeout(waitTimer);
      this.#setBusy(false);
      this.#abort = null;
      // Re-focus desktop seulement : en mobile, ne pas rouvrir le clavier.
      if (DESKTOP_MEDIA.matches) this.#input?.focus();
    }
  }

  /**
   * `source` dit d'OÙ vient la question : pilule suggérée, pilule de suivi, ou saisie
   * libre. C'est le signal qui répond à « quelles questions mettre en avant », et les
   * pouces ne le donnent pas : un pouce note la RÉPONSE. Sans ce champ, un clic sur
   * pilule et une question tapée arrivent au serveur strictement indistinguables, et
   * le palmarès du dashboard mesurerait n'importe quoi.
   */
  #pageContext(source) {
    const context = { url: window.location.pathname };
    if (source) context.source = source;
    const variantInput = document.querySelector('product-form input[name="id"]');
    const variantId = parseInt(variantInput?.value, 10);
    if (Number.isFinite(variantId)) context.variant_id = variantId;
    if (window.Theme?.template?.name) context.template = window.Theme.template.name;
    return context;
  }

  /**
   * Lecteur SSE minimal. EventSource est écarté : GET uniquement, pas de corps,
   * pas d'en-têtes. On lit donc le ReadableStream et on découpe sur la ligne vide.
   */
  async *#readEvents(stream) {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';

        for (const frame of frames) {
          const line = frame.split('\n').find((l) => l.startsWith('data:'));
          if (!line) continue;
          const payload = line.slice(5).trim();
          if (!payload || payload === '[DONE]') continue;
          try {
            yield JSON.parse(payload);
          } catch (error) {
            /* trame partielle ou heartbeat : on ignore */
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  // ---------------------------------------------------------------- produit

  /**
   * Carte produit posée dans le fil (direction 2b).
   *
   * LES DONNÉES VIENNENT DU LIQUID, PAS DU RÉSEAU. Le serveur n'émet qu'un signal nu
   * (« c'est le moment »), et tout le reste est lu sur le bloc. Trois raisons, dans
   * l'ordre : le service de chat n'a aucun client Shopify et n'a pas à en gagner un
   * pour afficher un prix ; Liquid formate déjà l'argent (`| money`), là où le JS n'a
   * pas le droit de le faire (note 3) ; et la fiche produit est de toute façon le seul
   * endroit où la carte a un sens.
   *
   * Hors PDP, `closest.product` est nul côté Liquid : aucun attribut n'est posé et la
   * carte ne rend pas. Un signal sans données n'est pas une erreur, c'est un bloc posé
   * ailleurs.
   *
   * `available` est la disponibilité AU RENDU de la page, pas « la vérité du moment ».
   * C'est assumé : le prix et le CTA de la colonne d'achat, à deux centimètres, datent
   * du même rendu, et le panier reste l'arbitre final, il refuse un article épuisé.
   *
   * Le bouton « Ajouter » est en FILET, jamais noir : il ne doit pas concurrencer le
   * « Ajouter au panier » de la page (cf. note 6 du bloc).
   */
  #appendProduct() {
    const data = this.dataset;
    if (!data.productTitle) return;

    // Le serveur promet déjà un signal par conversation, mais un fil repris peut le
    // faire retomber sur une carte déjà posée : deux gardes valent mieux qu'une
    // promesse tenue ailleurs.
    if (this.#productDone) return;
    this.#productDone = true;

    const el = document.createElement('div');
    el.className = 'chat-msg chat-msg--bot';

    const thumb = data.productImage
      ? `<div class="chat-card__thumb"><img class="chat-card__img" src="${escapeHtml(data.productImage)}" alt="" width="56" height="56" loading="lazy"></div>`
      : '';

    const available = data.productAvailable === 'true';
    const spec = [data.productSubtitle, available ? IN_STOCK_LABEL : OUT_OF_STOCK_LABEL]
      .filter(Boolean)
      .join(' · ');

    const cta =
      available && Number.isFinite(Number(data.productVariantId))
        ? `<button type="button" class="chat-card__cta" data-ref="card-add"
             data-variant-id="${escapeHtml(data.productVariantId)}">${escapeHtml(CTA_LABEL_FALLBACK)}</button>`
        : `<span class="chat-card__out">Indisponible</span>`;

    el.innerHTML = `
      <div class="chat-card">
        ${thumb}
        <div class="chat-card__body">
          <p class="chat-card__title">${escapeHtml(data.productTitle)}</p>
          ${spec ? `<p class="chat-card__spec">${escapeHtml(spec)}</p>` : ''}
          <div class="chat-card__row">
            <p class="chat-card__price">${escapeHtml(data.productPrice || '')}</p>
            ${cta}
          </div>
        </div>
      </div>`;

    this.#log?.appendChild(el);
    this.#scrollToEnd();
  }

  // ---------------------------------------------------------------- pouces

  /**
   * Pouces sous une réponse. `messageId` vient du serveur (`done`), il n'est jamais
   * fabriqué ici : sans lui, il n'y a rien à noter et la ligne n'apparaît pas.
   */
  #appendVote(msgEl, messageId) {
    const id = parseInt(messageId, 10);
    if (!msgEl || !Number.isFinite(id)) return;

    const el = document.createElement('div');
    el.className = 'chat-vote';
    el.dataset.messageId = String(id);
    el.innerHTML =
      `<span class="chat-vote__label">${escapeHtml(VOTE_LABEL)}</span>` +
      `<button type="button" class="chat-vote__btn" data-ref="vote" data-rating="up" aria-label="${escapeHtml(VOTE_UP_LABEL)}">${THUMB_UP_ICON}</button>` +
      `<button type="button" class="chat-vote__btn" data-ref="vote" data-rating="down" aria-label="${escapeHtml(VOTE_DOWN_LABEL)}">${THUMB_DOWN_ICON}</button>`;

    msgEl.appendChild(el);
    this.#scrollToEnd();
  }

  /**
   * Envoie le vote. L'accusé remplace les pouces TOUT DE SUITE, sans attendre le
   * serveur : un vote est un geste, pas une transaction, et faire patienter quelqu'un
   * sur un aller-retour dont il n'a rien à faire serait le punir d'avoir aidé.
   *
   * Un vote perdu ne produit donc aucun message d'erreur. C'est notre télémétrie, pas
   * son problème : de son point de vue, il a voté, et c'est vrai.
   */
  async #sendVote(button) {
    const row = button.closest('.chat-vote');
    const messageId = parseInt(row?.dataset.messageId, 10);
    const rating = button.dataset.rating;
    if (!Number.isFinite(messageId) || !rating) return;

    // Le bouton qui vient d'être cliqué porte peut-être le focus, et on le DÉTRUIT. Au
    // clavier, le focus retomberait alors sur <body> et le Tab suivant repartirait du
    // haut du document. L'accusé prend le relais : il est focusable juste ce qu'il faut
    // (tabindex -1, donc hors de l'ordre de tabulation), et le prendre le fait annoncer
    // par un lecteur d'écran, ce qui vaut confirmation du vote.
    // Seulement si le focus était bien là : le voler à une souris serait gratuit.
    const hadFocus = row.contains(document.activeElement);

    const thanks = document.createElement('p');
    thanks.className = 'chat-vote__thanks';
    thanks.textContent = VOTE_THANKS;
    thanks.tabIndex = -1;
    row.replaceWith(thanks);
    if (hadFocus) thanks.focus({ preventScroll: true });

    try {
      await fetch(`${this.dataset.endpoint}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: this.#conversationId,
          message_id: messageId,
          rating,
        }),
      });
    } catch (error) {
      /* cf. docstring : un vote perdu reste silencieux */
    }
  }

  /** 3b · trace de l'ajout, posée sous la carte (et non dans le bouton, qui se réarme). */
  #appendCartConfirm(button, line) {
    const card = button.closest('.chat-card');
    if (!card || !line || card.parentElement?.querySelector('.chat-confirm')) return;

    const el = document.createElement('p');
    el.className = 'chat-confirm';
    el.innerHTML = `${CHECK_ICON}${escapeHtml(line)}`;
    card.after(el);
    this.#scrollToEnd();
  }

  /**
   * Pilules de suivi (2b), posées UNE SEULE FOIS après la première réponse.
   *
   * Elles sont fixes, écrites et relues dans les settings : rien ne les génère.
   * Et elles ne réapparaissent pas à chaque tour, précisément parce qu'elles ne
   * suivent PAS la conversation : figées, elles finiraient par proposer « Comment
   * le préparer ? » juste après une réponse sur la préparation.
   */
  #appendFollowups() {
    if (this.#followupsDone) return;
    this.#followupsDone = true;

    const items = (this.dataset.followups || '')
      .split('|~|')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!items.length) return;

    const el = document.createElement('div');
    el.className = 'chat-followups';
    el.innerHTML = items
      .map(
        (q) =>
          `<button type="button" class="chat-chip" data-ref="followup-chip" data-question="${escapeHtml(q)}">${escapeHtml(q)}</button>`
      )
      .join('');
    this.#log?.appendChild(el);
    this.#scrollToEnd();
  }

  /**
   * Sortie de secours sous les états `blocked` et `error` : pilules statiques
   * vers le contact, plus WhatsApp si le setting whatsapp_url est rempli (le
   * Liquid ne pose data-whatsapp-url que dans ce cas, zéro code mort sinon).
   */
  #appendContactActions(container) {
    if (!container || container.querySelector('.chat-actions')) return;

    const contactUrl = this.dataset.contactUrl;
    if (!contactUrl) return;

    const el = document.createElement('div');
    el.className = 'chat-actions';

    const contactLabel = this.dataset.contactLabel || CONTACT_CTA_LABEL;
    let html = `<a class="chat-btn" href="${escapeHtml(contactUrl)}">${MAIL_ICON}${escapeHtml(contactLabel)}</a>`;
    const whatsappUrl = this.dataset.whatsappUrl;
    if (whatsappUrl) {
      html += `<a class="chat-btn" href="${escapeHtml(whatsappUrl)}" target="_blank" rel="noopener">${WHATSAPP_ICON}${WHATSAPP_CTA_LABEL}</a>`;
    }

    el.innerHTML = html;
    container.appendChild(el);
  }

  // Délégation : les cartes, les votes et les suivis sont créés après coup.
  #onLogClick = (event) => {
    const add = event.target.closest('[data-ref="card-add"]');
    if (add) {
      this.#addToCart(add);
      return;
    }

    const vote = event.target.closest('[data-ref="vote"]');
    if (vote) {
      this.#sendVote(vote);
      return;
    }

    // Pilules de suivi : créées après coup, donc en délégation. Le panneau est déjà
    // ouvert (elles n'existent que dans le fil) : on demande directement.
    const followup = event.target.closest('[data-ref="followup-chip"]');
    if (followup?.dataset.question) this.#ask(followup.dataset.question, 'followup');
  };

  /**
   * Ajout au panier. Copie fidèle du contrat de header-drawer-buybox.js · y compris
   * les sections à re-rendre (sans « auto-gifts », les cadeaux ne se déclenchent pas)
   * et le délai de 180 ms.
   */
  async #addToCart(button) {
    const variantId = parseInt(button.dataset.variantId, 10);
    if (!Number.isFinite(variantId) || button.disabled) return;

    button.disabled = true;
    button.classList.add('is-loading');
    let added = false;
    let confirmLine = '';

    const sections = ['auto-gifts'];
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) sections.push(cartDrawer.dataset.sectionId);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sections.join(','),
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        this.#appendError(data.description || "Ajout au panier impossible.");
        return;
      }

      added = true;

      // Ligne de confirmation (3b) : « Ajouté au panier · 1 sachet · 34,90 € ».
      // Les deux segments variables viennent de sources RÉELLES, jamais fabriquées :
      // le libellé de variante ressort de la réponse panier (`variant_title` vaut
      // null, ou « Default Title », sur un produit mono-variante : on l'omet alors),
      // et le prix est relu sur la carte, où le serveur l'a déjà formaté. Le JS ne
      // formate jamais d'argent (cf. note 3 de l'en-tête).
      const variantLabel = data.items?.[0]?.variant_title;
      confirmLine = [
        CART_CONFIRM_PREFIX,
        variantLabel && variantLabel !== 'Default Title' ? variantLabel : null,
        button.closest('.chat-card')?.querySelector('.chat-card__price')?.textContent.trim(),
      ]
        .filter(Boolean)
        .join(' · ');

      const currentCount = document.querySelector('cart-icon')?.getCurrentItemCount() || 0;

      // En mobile le panneau tient le verrou de scroll : le fermer D'ABORD, puis
      // dispatcher à 180 ms pour que l'ouverture du cart drawer repose le verrou
      // APRÈS notre retrait (même race que header-drawer-buybox.js).
      // En desktop le panneau est non modal : rien à fermer, dispatch immédiat.
      const mustClose = this.#scrollLocked;
      if (mustClose) this.close();

      setTimeout(
        () => {
          document.dispatchEvent(
            new CartUpdatedEvent(
              {},
              {
                itemCount: currentCount + 1,
                source: 'chat-widget',
                sections: data.sections,
              }
            )
          );
        },
        mustClose ? CART_DISPATCH_DELAY : 0
      );
    } catch (error) {
      this.#appendError("Ajout au panier impossible.");
    } finally {
      button.classList.remove('is-loading');
      if (added) {
        // 3b. Le bouton passe « Ajouté » + check cacao pendant 2 s, puis redevient
        // cliquable (on peut vouloir en ajouter un second). La ligne de confirmation,
        // elle, RESTE : c'est une trace du fil, pas un état de bouton.
        const label = button.textContent;
        button.classList.add('is-added');
        button.innerHTML = `${CHECK_ICON}${escapeHtml(ADDED_LABEL)}`;
        this.#appendCartConfirm(button, confirmLine);
        setTimeout(() => {
          button.textContent = label;
          button.classList.remove('is-added');
          button.disabled = false;
        }, ADDED_LABEL_DELAY);
      } else {
        button.disabled = false;
      }
    }
  }
}

if (!customElements.get('chat-widget')) {
  customElements.define('chat-widget', ChatWidget);
}
